import { useCallback, useEffect, useState } from "react";

import { type CloudFlowNodeType, CloudFlowProvider, type NodeConfigApiParameters } from "@doitintl/cmp-models";

import { useApiContext } from "../../../../api/context";
import { cloudflowTexts } from "../../../../assets/texts";
import { useCustomerId } from "../../../../Components/hooks/useCustomerId";
import { useErrorSnackbar } from "../../../../Components/SharedSnackbar/SharedSnackbar.context";
import { consoleErrorWithSentry } from "../../../../utils";
import { useAWSAccounts } from "../../../Settings/AWS/hooks";
import { AccountStatus } from "../../../Settings/AWS/types";
import {
  type CloudPermissions,
  type NodeConfigs,
  type PermissionCheckResult,
  type PermissionMap,
  PermissionValidityStatus,
} from "../../types";
import { type NodeWitOutputModel } from "../ApiActionParametersForm/parameters/wrappers/ReferencedField/useReferencedFieldContext";
import { useCloudflowState } from "../Common/hooks/useCloudflowState";
import { useReferenceableNodes } from "../Common/hooks/useReferenceableNodes";
import {
  GCPHierarchicalResource,
  getGcpPermission,
  PERMISSIONS_PROJECT_ID_KEY,
  PROJECT_ID_VARIATIONS,
} from "../utils/gcpUtils";
import { useAWSPermissionsCheck, useCheckAllAccountPermissions } from "./hooks/useAWSPermissionsCheck";
import { useNodeConfigurationContext } from "./NodeConfigurationContext";

const validateAPIParams = (parameters: NodeConfigApiParameters) => {
  if (!parameters) {
    consoleErrorWithSentry("parameters are required for permissions validation");
    return false;
  }
  switch (parameters.provider) {
    case "AWS":
      if (!parameters.configurationValues?.accountId || typeof parameters.configurationValues?.accountId !== "string") {
        consoleErrorWithSentry("accountId is required for AWS permissions validation");
        return false;
      }
      break;
    case "GCP":
      if (
        typeof parameters.configurationValues?.serviceAccount !== "string" ||
        typeof parameters.configurationValues?.organization !== "string"
      ) {
        consoleErrorWithSentry("serviceAccount and organization are required for GCP permissions validation");
        return false;
      }
      break;
    default:
      consoleErrorWithSentry(`Unsupported provider: ${parameters.provider}`);
      return false;
  }

  return true;
};

export type GCPPermissionConfigValues = {
  organization: string;
  serviceAccount: string;
  hierarchicalResource: GCPHierarchicalResource;
  [key: string]: string | null | undefined;
};

export type AWSPermissionConfigValues = object;

export type DOITPermissionConfigValues = object;
export type PermissionConfigValues = GCPPermissionConfigValues | AWSPermissionConfigValues | DOITPermissionConfigValues;

export const isGcpPermissionConfigValues = (
  configValues: PermissionConfigValues
): configValues is GCPPermissionConfigValues => "organization" in configValues;

/*
 * This hook is used to get the permissions for the cloud provider
 * nodeConfig should contain the operationRef, accountId(AWS) organizationId(AWS) or serviceAccount(GCP),
 */
export const useCloudPermissions = ({
  permissionConfigValues,
}: {
  permissionConfigValues: PermissionConfigValues;
}): CloudPermissions => {
  const { isBlueprint, isDemoFlow } = useCloudflowState();
  const { nodeConfig, updateNode } = useNodeConfigurationContext<CloudFlowNodeType.ACTION>();
  const customerId = useCustomerId();
  const api = useApiContext();
  const showError = useErrorSnackbar(7);
  const [accounts, _, accountsLoading] = useAWSAccounts();
  const [result, setResult] = useState<PermissionCheckResult>({
    data: undefined,
    status: PermissionValidityStatus.INITIAL,
  });
  const [loading, setLoading] = useState<boolean>(false);

  const checkAWSPermissions = useAWSPermissionsCheck(nodeConfig.flowId);
  const checkAllAWSPermissions = useCheckAllAccountPermissions(nodeConfig.flowId);

  const validateGCPPermissions = useCallback(
    async ({
      customerId,
      serviceAccount,
      permissions,
      projectId,
    }: {
      customerId: string;
      serviceAccount: string;
      permissions: string[];
      projectId?: string;
    }) => {
      const response = await api.request<PermissionMap>({
        method: "POST",
        url: `/v1/customers/${customerId}/cloudflow/validate-gcp-permissions`,
        data: {
          serviceAccount,
          permissions,
          projectId,
          scopes: ["https://www.googleapis.com/auth/cloud-platform"],
        },
      });
      return response.data;
    },
    [api]
  );

  function getGCPCommand(
    nodeConfig: NodeConfigs<CloudFlowNodeType.ACTION>,
    permission: string,
    permissionConfigValues: PermissionConfigValues
  ) {
    if (nodeConfig?.parameters?.provider !== CloudFlowProvider.GCP) {
      throw new Error("Provider not supported");
    }

    if (!isGcpPermissionConfigValues(permissionConfigValues)) {
      return "";
    }

    const organizationValue = permissionConfigValues.organization;
    const hierarchicalResource = permissionConfigValues.hierarchicalResource;
    const orgId = organizationValue ? organizationValue.split("/").pop() : undefined;

    const projectIdKey = Object.keys(permissionConfigValues).find((key) =>
      [...PROJECT_ID_VARIATIONS, PERMISSIONS_PROJECT_ID_KEY].includes(key)
    );
    const projectId = projectIdKey ? permissionConfigValues[projectIdKey] : undefined;

    let scopeFlag = "";
    if (hierarchicalResource === GCPHierarchicalResource.PROJECT && projectId) {
      scopeFlag = `--project ${projectId}`;
    } else if (hierarchicalResource === GCPHierarchicalResource.ORGANIZATION && orgId) {
      scopeFlag = `--organization ${orgId}`;
    } else {
      return "";
    }

    return `gcloud iam roles update doit_cmp_role ${scopeFlag} --add-permissions ${permission}`;
  }

  const updatePermissionsError = useCallback(
    (valid: boolean) => {
      updateNode((prevNode) => {
        const errors = { ...prevNode.errors };
        if (valid) {
          delete errors.permission_error;
        } else {
          errors.permission_error = cloudflowTexts.STATUS.PERMISSION_ERROR;
        }

        return { errors };
      });
    },
    [updateNode]
  );

  const updatePermissions = useCallback(async () => {
    if (isBlueprint || isDemoFlow) {
      setResult({ status: PermissionValidityStatus.VALID, data: undefined });
      return;
    }
    if (!nodeConfig.parameters) {
      return;
    }

    if (!validateAPIParams(nodeConfig.parameters)) {
      showError("Invalid parameters for permissions validation");
      return;
    }

    let permissionMap: PermissionMap = {};
    const operation = nodeConfig.parameters.operation;
    setLoading(true);
    try {
      switch (nodeConfig.parameters.provider) {
        case "AWS":
          {
            if (nodeConfig.parameters.configurationValues.accountId === "All accounts") {
              const validAccounts = accounts.filter((a) => a.status === AccountStatus.healthy);
              if (validAccounts.length === 0) {
                throw new Error("no connected AWS accounts selected");
              }

              const res = await checkAllAWSPermissions(
                nodeConfig.parameters,
                accounts.filter((a) => a.status === AccountStatus.healthy)
              );

              const hasFailedCheck = res.some((r) => !r.checkResult.permissionExists);
              if (!hasFailedCheck) {
                setResult({ status: PermissionValidityStatus.VALID, data: undefined });
                break;
              }

              setResult({
                status: PermissionValidityStatus.INVALID,
                data: res.map((r) => ({
                  accountName: r.accountName,
                  command: r.checkResult.commandToGrantPermission,
                  requiredPermissions: [r.checkResult.awsPermission],
                  hasPermission: r.checkResult.permissionExists,
                })),
              });

              updatePermissionsError(!hasFailedCheck);
              break;
            }

            // NOTE: node config params arent being narrowed in the filter so creating accountId here
            const accountId = nodeConfig.parameters.configurationValues.accountId;
            const account = accounts.find((a) => a.status === AccountStatus.healthy && a.accountId === accountId);

            if (!account) {
              throw new Error("selected AWS Account not found");
            }

            const { permissionExists, awsPermission, commandToGrantPermission } = await checkAWSPermissions(
              nodeConfig.parameters,
              account
            );

            if (!permissionExists) {
              setResult({
                data: { requiredPermissions: [awsPermission], command: commandToGrantPermission },
                status: PermissionValidityStatus.INVALID,
              });
            } else {
              setResult({ data: undefined, status: PermissionValidityStatus.VALID });
            }

            updatePermissionsError(permissionExists);
          }
          break;
        case "GCP":
          {
            const gcpPermission = getGcpPermission(operation);
            if (!isGcpPermissionConfigValues(permissionConfigValues)) {
              throw new Error("Invalid permission config values");
            }
            const projectId = permissionConfigValues.permissionsProjectId;

            permissionMap = await validateGCPPermissions({
              customerId,
              serviceAccount: nodeConfig.parameters.configurationValues.serviceAccount,
              permissions: [gcpPermission],
              ...(projectId && {
                projectId,
              }),
            });

            if (!permissionMap) {
              setResult({ status: PermissionValidityStatus.UNABLE_TO_VERIFY, data: undefined });
            } else {
              const filteredPermissions = Object.entries(permissionMap)
                .filter(([, value]) => !value)
                .map(([key]) => key);

              if (filteredPermissions.length > 0) {
                const command = getGCPCommand(nodeConfig, gcpPermission, permissionConfigValues);

                setResult({
                  status: PermissionValidityStatus.INVALID,
                  data: { requiredPermissions: filteredPermissions, command },
                });
              } else {
                setResult({ status: PermissionValidityStatus.VALID, data: undefined });
              }

              updatePermissionsError(filteredPermissions.length === 0);
            }
          }
          break;
        default:
          consoleErrorWithSentry(`Unsupported provider`);
      }
    } catch (error: any) {
      showError(`Failed to verify permissions, error: ${error.message}`);
      consoleErrorWithSentry(error);
    } finally {
      setLoading(false);
    }
  }, [
    isBlueprint,
    isDemoFlow,
    nodeConfig,
    showError,
    checkAWSPermissions,
    updatePermissionsError,
    checkAllAWSPermissions,
    permissionConfigValues,
    validateGCPPermissions,
    customerId,
    accounts,
  ]);

  const resetPermissions = useCallback(() => {
    setResult({ status: PermissionValidityStatus.INITIAL, data: undefined });
  }, []);

  useEffect(() => {
    if (accountsLoading) return;

    if (nodeConfig.errors.permission_error) {
      // Wait for the hierarchicalResource to be set in the permissionConfigValues before calling updatePermissions for GCP
      if (
        nodeConfig.parameters?.provider === "GCP" &&
        isGcpPermissionConfigValues(permissionConfigValues) &&
        (!permissionConfigValues.hierarchicalResource ||
          !permissionConfigValues.serviceAccount ||
          !permissionConfigValues.organization)
      ) {
        return;
      }
      updatePermissions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionConfigValues, accountsLoading]);

  return { result, updatePermissions, loading, resetPermissions };
};

export const useNodeDataSourceUpdate = ({ nodeId, referencedNodeId }: { nodeId: string; referencedNodeId: string }) => {
  const [referenceableNodes] = useReferenceableNodes(nodeId);

  const [selectedNode, setSelectedNode] = useState<NodeWitOutputModel | undefined>();
  const [switchDataSourceOpen, setSwitchDataSourceOpen] = useState(false);
  const [handleConfirm, setHandleConfirm] = useState<(() => Promise<any>) | undefined>();
  const [handleClose, setHandleClose] = useState<(() => Promise<any>) | undefined>();

  useEffect(() => {
    const preselectedNode = referenceableNodes.find((node) => node.id === referencedNodeId);
    setSelectedNode(preselectedNode);
  }, [referencedNodeId, referenceableNodes]);

  const confirmSwitchDataSource = useCallback(
    async (onConfirm: (() => Promise<void>) | undefined, onClose: (() => Promise<void>) | undefined): Promise<void> => {
      setSwitchDataSourceOpen(true);
      const handleConfirmWrapper = async () => {
        setSwitchDataSourceOpen(false);
        if (onConfirm) {
          await onConfirm();
        }
      };
      const handleCloseWrapper = async () => {
        setSwitchDataSourceOpen(false);
        if (onClose) {
          await onClose();
        }
      };
      setHandleConfirm(() => handleConfirmWrapper);
      setHandleClose(() => handleCloseWrapper);
    },
    []
  );

  return {
    selectedNode,
    setSelectedNode,
    referenceableNodes,
    switchDataSourceOpen,
    setSwitchDataSourceOpen,
    handleConfirm,
    handleClose,
    confirmSwitchDataSource,
  };
};
